import { MouseEvent } from 'react';

import { Scatter as RechartsScatter } from 'recharts';

import { ScatterShape } from './ScatterShape';
import { ScatterProps } from './types';
import { ANIMATION_DURATION_MS } from '../../_constants';
import { useChartConfig } from '../../ChartConfigProvider';
import { useChartContext } from '../../ChartProvider';

type ShapeClickHandlerParams<T> = {
  index: number;
  event: MouseEvent;
  payload: T;
  isActive: boolean;
};

export const Scatter = <T, V extends string | number = string | number>({
  isAnimationActive,
  hideActiveShape = false,
  testId = 'chart-scatter',
  shapeProps,
  onActiveShapeClick,
  onShapeClick,
  dataKey,
  ...rest
}: ScatterProps<T, V>) => {
  const chartConfig = useChartConfig({ isAnimationActive });
  const { highlightingEnabled, onHighlightChange, highlightedDatapoint } =
    useChartContext();

  const handleShapeClick = ({
    index,
    event,
    payload,
    isActive,
  }: ShapeClickHandlerParams<T>) => {
    if (highlightingEnabled) {
      onHighlightChange(String(index));
    }

    if (isActive) {
      onActiveShapeClick?.(payload, index, event);
    } else {
      onShapeClick?.(payload, index, event);
    }
  };

  return (
    <RechartsScatter
      {...rest}
      dataKey={dataKey}
      shape={
        <ScatterShape<T, V>
          highlightedDatapoint={highlightedDatapoint}
          shapeProps={shapeProps}
          dataKey={dataKey as keyof T}
          onClick={
            highlightingEnabled
              ? (payload, index, event) =>
                  handleShapeClick({ payload, event, index, isActive: false })
              : onShapeClick
          }
          testId={`${testId}-shape`}
        />
      }
      activeShape={
        hideActiveShape ? (
          false
        ) : (
          <ScatterShape<T, V>
            highlightedDatapoint={highlightedDatapoint}
            shapeProps={shapeProps}
            dataKey={dataKey as keyof T}
            onClick={
              highlightingEnabled
                ? (payload, index, event) =>
                    handleShapeClick({ payload, event, index, isActive: true })
                : onActiveShapeClick
            }
            testId={`${testId}-active-shape`}
            isActive
          />
        )
      }
      isAnimationActive={chartConfig.isAnimationActive}
      animationDuration={ANIMATION_DURATION_MS}
    />
  );
};
